import React, { useEffect, useState } from "react";
import { getDownloadURL, ref as storageRef, uploadString } from "firebase/storage";
import { storage } from "../../../helpers/firebaseConfig";
import "./MBKnowledgeTab.css";

export const knowledgeStoragePath = (simulationId) =>
  `model_builders/${simulationId}/knowledge/domain_knowledge.md`;

export default function MBKnowledgeTab({ simulationId }) {
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const draftKey = `builder-knowledge-draft:${simulationId}`;
  const isDirty = content !== savedContent;

  useEffect(() => {
    if (!simulationId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setMessage("");
    const load = async () => {
      let remoteContent = "";
      try {
        const url = await getDownloadURL(storageRef(storage, knowledgeStoragePath(simulationId)));
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Could not load knowledge (HTTP ${response.status}).`);
        remoteContent = await response.text();
      } catch (loadError) {
        if (loadError?.code !== "storage/object-not-found") throw loadError;
      }
      if (cancelled) return;
      setSavedContent(remoteContent);
      try {
        const draft = sessionStorage.getItem(draftKey);
        if (draft === remoteContent) sessionStorage.removeItem(draftKey);
        setContent(draft === null ? remoteContent : draft);
      } catch (_) {
        setContent(remoteContent);
      }
    };
    load().catch((loadError) => {
      if (!cancelled) setError(String(loadError?.message || loadError));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [simulationId, draftKey]);

  const editContent = (value) => {
    setContent(value);
    setMessage("");
    try {
      if (value === savedContent) sessionStorage.removeItem(draftKey);
      else sessionStorage.setItem(draftKey, value);
    } catch (_) {}
  };

  const save = async () => {
    if (!simulationId || loading || saving || !isDirty) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await uploadString(storageRef(storage, knowledgeStoragePath(simulationId)), content, "raw", {
        contentType: "text/markdown; charset=utf-8",
      });
      setSavedContent(content);
      try { sessionStorage.removeItem(draftKey); } catch (_) {}
      setMessage("Saved for this simulation.");
    } catch (saveError) {
      setError(String(saveError?.message || saveError));
    } finally {
      setSaving(false);
    }
  };

  return <section className="mb-knowledgePanel">
    <header className="mb-knowledgeHeader">
      <div>
        <h2>Simulation knowledge</h2>
        <p>Add definitions, assumptions, local terminology, constraints, and other useful context about this simulation.</p>
      </div>
      <button type="button" onClick={save} disabled={loading || saving || !isDirty}>
        {saving ? "Saving…" : "Save knowledge"}
      </button>
    </header>
    <label htmlFor="mb-knowledge-editor">Knowledge notes</label>
    <textarea
      id="mb-knowledge-editor"
      value={content}
      onChange={(event) => editContent(event.target.value)}
      disabled={loading || saving || (!!error && !isDirty)}
      placeholder="Add custom knowledge for the agent to access…"
      spellCheck
    />
    <div className="mb-knowledgeFooter">
      <span>{loading ? "Loading…" : error ? "Could not load or save knowledge" : isDirty ? "Unsaved changes" : "No unsaved changes"}</span>
      {message && <span role="status">{message}</span>}
      {error && <span className="mb-knowledgeError" role="alert">{error}</span>}
    </div>
  </section>;
}
